import cookie from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import { EnumMarketplace } from "../../../../../../server/routes/marketplace/types";
import {
    EnumStatisticChartGroupBy, EnumStatisticChartGroupByPeriod,
    EnumStatisticChartPeriod,
    EnumStatisticChartType,
    EnumStatisticStatusGroup
} from "../../../../../../server/routes/statistic/types";
import useMarketplacesFilter from "../../../../components/FilterMarketplacesProvider/useFilterMarketplaces";
import getToken from "../../../../getters/getToken";
import getStatisticChart from "../../../../requests/statistic/getChart";
import {
    actionLoadingFail,
    actionLoadingStart,
    actionLoadingSuccessfully
} from "../../../../store/statisticChart/actions";
import { useStatusGroup } from "../../StatusGroupProvider";
import getStatisticChartAnyDate from "../../../../requests/statistic/getChart/getStatisticChartAnyDate";
import moment from "moment";
import useAlerts from "../../../../hooks/useAlerts";

export type Loader = (params?: {
    type?: EnumStatisticChartType;
    period?: EnumStatisticChartPeriod;
    fromDate?: Date;
    toDate?: Date;
    marketplaceTypes?: EnumMarketplace[];
    hideFakeOrders?: boolean;
    groupBy?: EnumStatisticChartGroupBy;
    groupByPeriod?: EnumStatisticChartGroupByPeriod;
}) => Promise<void>;

const useLoadingAnyDateChart = (hideFakeOrders?: boolean): Loader => {
    const { statusGroup, additionalChart } = useStatusGroup();
    const alerts = useAlerts();
    const { type, fromDate, toDate, groupBy, groupByPeriod } = useSelector(
        ({
             statisticChart: {
                 [statusGroup]: { groupBy, type, fromDate, toDate, groupByPeriod },
             },
         }) => ({ type, fromDate, toDate, groupBy, groupByPeriod })
    );
    const {
        active: filteringMarketplaces,
        isLoading: filteringMarketplacesloading,
    } = useMarketplacesFilter();
    const dispatch = useDispatch();
    const token = getToken();
    const factoryGetterStatisticChart = (
        statusGroup: EnumStatisticStatusGroup | "orders_received",
        params: Parameters<Loader>[0]
    ) =>
        getStatisticChartAnyDate(token, statusGroup, {
            fromDate: moment(fromDate).format('YYYY-MM-DD'),
            toDate: moment(toDate).format('YYYY-MM-DD'),
            chartType: params?.type ?? type,
            marketplaceTypes:
                filteringMarketplaces.length > 0 ? filteringMarketplaces : undefined,
            hideFakeOrders: params?.hideFakeOrders ?? hideFakeOrders ?? false,
            groupByPeriod: params?.groupByPeriod ?? groupByPeriod,
            groupBy:
                params?.groupBy ??
                (cookie.get(
                    "statistic.chart." + statusGroup + ".groupBy"
                ) as EnumStatisticChartGroupBy) ??
                groupBy ??
                EnumStatisticChartGroupBy.CREATED_DATE,
        });
    return (params) => {
        dispatch(actionLoadingStart(statusGroup));
        if (filteringMarketplacesloading) {
            return Promise.resolve();
        }
        return Promise.all(
            [statusGroup, ...(additionalChart ?? [])].map((status) =>
                factoryGetterStatisticChart.call(null, status, params)
            )
        )
            .then(([statusGroupChartData, ...additionalChartDatas]) => {
                if (statusGroupChartData.status === "OK") {
                    dispatch(
                        actionLoadingSuccessfully({
                            statusGroup,
                            list: statusGroupChartData.result.data,
                        })
                    );
                }

                if (statusGroupChartData.status === "ERROR" && statusGroupChartData.errors) {
                    statusGroupChartData.errors.forEach(({message}) => {
                        alerts({
                            variant: 'error',
                            message: `${message}`,
                            autoHideDuration: 5000,
                        })
                    })
                }

                additionalChart &&
                additionalChartDatas.forEach(({ result, status, errors }, index) => {
                    if (!additionalChart[index]) {
                        return;
                    }
                    if (status === "OK") {
                        dispatch(
                            actionLoadingSuccessfully({
                                statusGroup: additionalChart[index],
                                list: result.data,
                            })
                        );
                    }

                    if (status === "ERROR" && errors) {
                        errors.forEach(({message}) => {
                            alerts({
                                variant: 'error',
                                message: `${message}`,
                                autoHideDuration: 5000,
                            })
                        })
                    }

                });
            })
            .catch(({ message }: Error) => {
                dispatch(actionLoadingFail({ statusGroup, error: message }));
            });
    };
};

export default useLoadingAnyDateChart;
